import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
} from '@floating-ui/react-dom';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Field,
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import {
  SelectorIcon as ChevronUpDownIcon,
  PlusIcon,
} from '@heroicons/react/solid';
import classNames from 'classnames';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SvgPlaceholderImage from '~/PlaceholderImage';
import { unique } from '~/helpers/array';
import { by, notUndefined } from '~/helpers/filter';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { styles } from '../FindAddContact/ContactSelect';
import { SearchInput } from '../FindAddContact/SearchInput';
import { PopoverStateChangedEffect } from '../PopoverStateChangedEffect';
import { BuildingPlaceholder } from '../ui/BuildingPlaceholder';
import { FacetedFilterSelect } from '../ui/FacetedFilterSelect';
import { MenuTransition } from '../ui/Transition/MenuTransition';
import { Site } from './FindAddSite';
import { getAttributeFilterOptions } from './siteAttributeFilters';
export type SiteSelectProps = {
  label?: string;
  error?: string;
  sites?: Site[];
  value?: Site[] | string[];
  onChange: (newValue: Site[]) => void;
  onCreate?: (query: string) => void;
  multiple?: boolean;
  required?: boolean;
  optionsBoxHeight?: string;
  /** When true, forces simple single-selection mode, overriding complex multiple selection logic */
  forceSingleMode?: boolean;
  /** Indicates if sites are currently being loaded from the server */
  loading?: boolean;
};

const isString = (value: unknown): value is string => typeof value === 'string';
const Image = ({ url }: { url?: string | null }) => (
  <div className='flex h-6 w-6 flex-shrink-0 items-center justify-center overflow-hidden rounded-[3px]'>
    {url ? (
      <img className='h-full w-full object-cover' alt='' src={url} />
    ) : (
      <span className='bg-white'>
        <SvgPlaceholderImage className='h-[22px] w-[22px] fill-current text-grey-30' />
      </span>
    )}
  </div>
);

export function SiteSelect({
  label,
  error,
  sites = [],
  value = [],
  onChange,
  onCreate,
  multiple = false,
  required = false,
  forceSingleMode = false,
  loading = false,
  ...props
}: SiteSelectProps) {
  const { t } = useTranslation();
  const listboxOptionsRef = useRef<HTMLElement>(null);
  const confirmBtnRef = useRef<HTMLButtonElement>(null);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const { isMobile } = useBreakpoint();
  const DISPLAY_LIMIT = isMobile ? 3 : 6;

  const [filterValues, setFilterValues] = useState<
    { key: string; value: string[] }[]
  >([]);
  // Debounce the search query to prevent excessive filtering while typing
  // This creates a stable debounced function that won't change between renders
  const debouncedSetQuery = useMemo(
    () =>
      debounce((searchQuery: string) => {
        setDebouncedQuery(searchQuery);
      }, 150),
    [] // Empty deps: we want the same debounced function instance throughout component lifecycle
  );

  useEffect(() => {
    debouncedSetQuery(query);
    return () => {
      debouncedSetQuery.cancel(); // ✅ Critical: Cancel pending debounced calls to prevent memory leaks
    };
  }, [query, debouncedSetQuery]); // debouncedSetQuery is stable but included for ESLint rules
  // Convert value prop to selected Site objects, filtering out invalid entries
  const selected = useMemo(() => {
    return (value || [])
      .map((val) => {
        return isString(val) ? sites.find((site) => site.id === val) : val;
      })
      .filter(notUndefined);
  }, [value, sites]); // Both value and sites can change, affecting the selected sites

  // Initialize multiple mode based on whether multiple items are selected
  // Use value prop directly to avoid circular dependency with 'selected'
  const [isMultipleMode, setIsMultipleMode] = useState(() => {
    return (value || []).length > 1;
  });

  // Sync isMultipleMode state with actual selection count
  useEffect(() => {
    if (selected.length > 1 && !isMultipleMode) {
      setIsMultipleMode(true);
    } else if (selected.length <= 1 && isMultipleMode) {
      setIsMultipleMode(false);
    }
  }, [selected.length, isMultipleMode]); // Both dependencies needed to detect state mismatches

  // Floating UI configuration for dropdown positioning and sizing
  const { refs, floatingStyles } = useFloating({
    open: isOpen,
    placement: 'bottom-start',
    strategy: 'absolute',
    middleware: [
      offset(6),
      size({
        apply({ availableHeight, elements, rects }) {
          // Match reference width and set maxHeight to available space minus fixed button
          const adjustedHeight = Math.max(0, availableHeight - 96); // Account for fixed footer button
          const minHeight = 350; // Minimum acceptable height

          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            // If we have less than minHeight, set maxHeight to minHeight to trigger flip
            // Otherwise use the available space
            maxHeight:
              availableHeight < minHeight
                ? `${minHeight}px`
                : `${adjustedHeight}px`,
          });
        },
        padding: 6,
      }),
      flip({
        fallbackStrategy: 'initialPlacement',
        fallbackPlacements: ['top-start', 'bottom-end', 'top-end'],
        padding: 6,
      }),
      shift({ padding: 6 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const isItemSelected = useCallback(
    (site: Site) => {
      return selected.some((s) => s.id === site.id);
    },
    [selected] // Only depends on selected array - when it changes, selection logic updates
  );
  // Pre-compute selected IDs to avoid repeated .map() calls in other computations
  const selectedIds = useMemo(() => selected.map(({ id }) => id), [selected]);
  // Memoize expensive search and filter operations to prevent unnecessary re-computations
  const searched = useMemo(() => {
    return debouncedQuery === ''
      ? sites
      : sites.filter(by(debouncedQuery, ['name']));
  }, [debouncedQuery, sites]); // Re-search when query or sites change
  const filtered = useMemo(() => {
    return searched.filter(({ attributes: siteAttributes }) =>
      filterValues.every(({ key, value }) =>
        value.some((v) =>
          siteAttributes?.find(({ name }) => name === key)?.value.includes(v)
        )
      )
    );
  }, [searched, filterValues]); // Re-filter when search results or filter criteria change
  const allFilteredSelected = useMemo(() => {
    return filtered.every((site) => selectedIds.includes(site.id));
  }, [filtered, selectedIds]); // Check if all filtered items are selected
  // Expensive attribute processing - only recalculate when sites change
  const attributeFilters = useMemo(
    () => getAttributeFilterOptions(sites),
    [sites] // Only recalculate filter options when sites data changes
  );

  // Stable callback for closing the dropdown
  const handleClose = useCallback(() => {
    const escEvent = new KeyboardEvent('keydown', {
      key: 'Escape',
      code: 'Escape',
      keyCode: 27,
      which: 27,
      bubbles: true,
      cancelable: true,
    });
    listboxOptionsRef.current?.dispatchEvent(escEvent);
  }, []); // No dependencies: DOM refs are stable, and KeyboardEvent creation doesn't depend on state

  // ✅ CRITICAL: handleChange must be defined BEFORE toggleSelectAll and handleItemClick
  // because they reference it in their dependency arrays and function bodies
  const handleChange = useCallback(
    (newValue: Site | Site[], fromCheckbox = false) => {
      if (forceSingleMode) {
        if (!Array.isArray(newValue) && newValue !== null) {
          onChange([newValue]);
          handleClose();
        }
        return;
      }
      if (fromCheckbox && !isMultipleMode) {
        setIsMultipleMode(true);
      }
      if (Array.isArray(newValue)) {
        if (newValue.every((site) => site !== null)) {
          onChange(newValue);
        }
      } else if (newValue !== null) {
        if (isMultipleMode) {
          const isSelected = selected.some((site) => site.id === newValue.id);
          onChange(
            isSelected
              ? selected.filter((site) => site.id !== newValue.id)
              : [...selected, newValue]
          );
        } else {
          onChange([newValue]);
          handleClose();
        }
      }
    },
    [forceSingleMode, onChange, handleClose, isMultipleMode, selected]
    // setIsMultipleMode not included: React setState functions are stable references
  );

  const toggleSelectAll = useCallback(() => {
    if (!forceSingleMode && !isMultipleMode) {
      setIsMultipleMode(true);
    }
    if (allFilteredSelected) {
      handleChange(
        selected.filter(
          ({ id: selectedId }) =>
            !filtered
              .map(({ id: filteredId }) => filteredId)
              .includes(selectedId)
        ),
        true
      );
    } else {
      handleChange(unique([...selected, ...filtered]), true);
    }
  }, [
    forceSingleMode,
    isMultipleMode,
    allFilteredSelected,
    selected,
    filtered,
    handleChange,
  ]);

  const handleItemClick = useCallback(
    (site: Site, fromCheckbox: boolean) => {
      if (forceSingleMode) {
        onChange([site]);
        handleClose();
        return;
      }
      if (fromCheckbox || isMultipleMode) {
        const isSelected = selected.some((s) => s.id === site.id);
        const newSelection = isSelected
          ? selected.filter((s) => s.id !== site.id)
          : [...selected, site];
        onChange(newSelection);
      } else {
        // Check if we already have a selection and user is clicking a different site
        const isSelected = selected.some((s) => s.id === site.id);
        if (selected.length > 0 && !isSelected) {
          // Switch to multiple mode and add the new site
          setIsMultipleMode(true);
          onChange([...selected, site]);
        } else if (isSelected) {
          // If clicking the same selected site, deselect it
          onChange([]);
          handleClose();
        } else {
          // First selection or replacing single selection
          onChange([site]);
          handleClose();
        }
      }
    },
    [forceSingleMode, onChange, handleClose, isMultipleMode, selected]
    // All dependencies included: function behavior depends on all these values
  );
  // NOW SAFE TO HAVE EARLY RETURNS - ALL HOOKS HAVE BEEN CALLED
  // Handle different empty states with proper UX messaging
  if (loading) {
    return (
      <Field>
        {label && (
          <Label className={styles.label}>
            {label}
            {required && <span className='pl-0.5 text-brand'>*</span>}
          </Label>
        )}
        <div className={classNames(styles.control, 'py-3')}>
          <span className='text-gray-500'>Loading sites...</span>
        </div>
        {error && <p className='ErrorMessage'>{error}</p>}
      </Field>
    );
  }
  if (!sites || sites.length === 0) {
    return (
      <Field>
        {label && (
          <Label className={styles.label}>
            {label}
            {required && <span className='pl-0.5 text-brand'>*</span>}
          </Label>
        )}
        <div className={classNames(styles.control, 'py-3')}>
          <span className='text-gray-500'>No sites available</span>
        </div>
        {error && <p className='ErrorMessage'>{error}</p>}
      </Field>
    );
  }

  return (
    <Field>
      {label && (
        <Label className={styles.label}>
          {label}
          {required && <span className='pl-0.5 text-brand'>*</span>}
        </Label>
      )}
      <Listbox
        value={forceSingleMode ? selected[0] || null : selected}
        onChange={handleChange}
        multiple={forceSingleMode ? false : isMultipleMode}
      >
        {({ open }) => (
          <>
            <PopoverStateChangedEffect open={open} onOpenChange={setIsOpen} />
            <ListboxButton
              ref={refs.setReference}
              aria-label='Select a site'
              className={classNames(styles.control, {
                'mb-5': label && !error,
                'py-1': selected.length,
                'py-3': !selected.length,
                'border-red-500': Boolean(error),
              })}
            >
              <div className='flex items-center gap-3'>
                <div className='flex flex-1 flex-wrap gap-2 overflow-x-clip text-left'>
                  {forceSingleMode ? (
                    selected.length > 0 ? (
                      <div
                        className={classNames(
                          styles.selectedOption,
                          'rounded-md'
                        )}
                      >
                        {selected[0].image ? (
                          <Image url={selected[0].image} />
                        ) : (
                          <BuildingPlaceholder className='!w-6 rounded-[3px] px-0 text-base' />
                        )}
                        <span className='truncate'>{selected[0].name}</span>
                        <div
                          className='relative h-3.5 w-3.5 flex-shrink-0 cursor-pointer rounded-full bg-[#808490] transition-colors hover:bg-[#6D6E7A]'
                          onClick={(e) => {
                            e.stopPropagation();
                            onChange([]);
                          }}
                          role='button'
                          tabIndex={0}
                          aria-label='Remove selection'
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              onChange([]);
                            }
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faXmark}
                            className='absolute left-0 top-0 h-2.5 w-2.5 p-0.5 text-white'
                          />
                        </div>
                      </div>
                    ) : (
                      <span className='text-gray-500'>Select a site...</span>
                    )
                  ) : (
                    <>
                      {selected.slice(0, DISPLAY_LIMIT).map((site) => (
                        <div
                          key={site.id}
                          className={classNames(
                            styles.selectedOption,
                            'rounded-md'
                          )}
                        >
                          {site.image ? (
                            <Image url={site.image} />
                          ) : (
                            <BuildingPlaceholder className='!w-6 rounded-[3px] px-0 text-base' />
                          )}
                          <span className='truncate'>{site.name}</span>
                          <div
                            aria-label='Remove selection'
                            className='relative h-3.5 w-3.5 flex-shrink-0 cursor-pointer rounded-full bg-[#808490] transition-colors hover:bg-[#6D6E7A]'
                            onClick={(e) => {
                              e.stopPropagation();
                              handleChange(
                                selected.filter(({ id }) => id !== site.id)
                              );
                            }}
                            role='button'
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                handleChange(
                                  selected.filter(({ id }) => id !== site.id)
                                );
                              }
                            }}
                          >
                            <FontAwesomeIcon
                              icon={faXmark}
                              className='absolute left-0 top-0 h-2.5 w-2.5 p-0.5 text-white'
                            />
                          </div>
                        </div>
                      ))}
                      {selected.length > DISPLAY_LIMIT && (
                        <div
                          className={classNames(
                            styles.selectedOption,
                            'rounded-md bg-grey-5'
                          )}
                        >
                          <span className='truncate text-grey-50'>
                            +{selected.length - DISPLAY_LIMIT} more
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div>
                  <ChevronUpDownIcon
                    className='h-5 w-5 flex-shrink-0 text-gray-400'
                    aria-hidden='true'
                  />
                </div>
              </div>
            </ListboxButton>
            <MenuTransition>
              <ListboxOptions
                ref={(el) => {
                  // Set both refs: the internal ref and Floating UI ref
                  (
                    listboxOptionsRef as React.MutableRefObject<HTMLElement | null>
                  ).current = el;
                  refs.setFloating(el);
                }}
                portal={false}
                style={{
                  ...floatingStyles,
                  // Hide until positioned to prevent flash at 0,0 on first open
                  visibility: floatingStyles.transform ? 'visible' : 'hidden',
                }}
                className={classNames(
                  props.optionsBoxHeight,
                  multiple && 'pb-0',
                  'z-[1000] overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm',
                  'flex flex-col'
                )}
              >
                <div className='sticky z-50  mb-1 bg-white p-1 pb-0'>
                  <SearchInput
                    className='rounded-full border-none bg-grey-3'
                    inputProps={{ className: 'bg-grey-3' }}
                    placeholder={t('site_plural')}
                    setQuery={setQuery}
                    query={query}
                  />
                  {!forceSingleMode && (
                    <FacetedFilterSelect
                      inFilterBar={false}
                      label='Filter by Attribute'
                      options={attributeFilters}
                      value={filterValues}
                      onChange={setFilterValues}
                      isAttributeFilter={true}
                    />
                  )}
                </div>
                <div className='flex-grow overflow-y-auto'>
                  {onCreate && (
                    <ListboxOption
                      key='new'
                      value={null}
                      className={({ active }) =>
                        classNames(
                          'relative cursor-default select-none text-primary active:bg-shade-12',
                          active && 'bg-shade-10'
                        )
                      }
                      onClick={() => onCreate(query)}
                    >
                      {({ focus }) => (
                        <div
                          className={`flex items-center gap-2 truncate whitespace-nowrap px-4 py-3 ${
                            focus ? 'font-medium' : 'font-normal'
                          }`}
                        >
                          <div className=''>
                            <PlusIcon
                              className='h-5 w-5 text-brand'
                              aria-hidden='true'
                            />
                          </div>
                          <div className='text-base'>{t('createSite')}</div>
                        </div>
                      )}
                    </ListboxOption>
                  )}
                  {!forceSingleMode && filtered.length > 0 && (
                    <ListboxOption
                      key='all'
                      value={null}
                      className={({ active }) =>
                        classNames(
                          'relative cursor-default select-none text-primary active:bg-shade-12',
                          active && 'bg-shade-10'
                        )
                      }
                    >
                      {() => (
                        <div
                          className='flex items-center'
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSelectAll();
                          }}
                        >
                          <div
                            className='flex items-center justify-center pl-4'
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSelectAll();
                            }}
                          >
                            <div className='relative'>
                              <input
                                className={classNames(
                                  'z-30 h-4 w-4 cursor-pointer accent-brand',
                                  {
                                    invisible: allFilteredSelected,
                                  }
                                )}
                                type='checkbox'
                                checked={false}
                                onChange={() => {}}
                              />
                              {allFilteredSelected && (
                                <div className='absolute inset-0 flex h-4 w-4 cursor-pointer items-center justify-center rounded border border-brand bg-brand text-white'>
                                  <span className='mb-0.5 text-lg font-medium leading-none'>
                                    -
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div
                            className={classNames(
                              'flex flex-1 items-center justify-between truncate px-2 py-2',
                              {
                                'bg-[#00707014]':
                                  allFilteredSelected && isMultipleMode,
                              }
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSelectAll();
                            }}
                          >
                            <div className='flex items-center gap-2 truncate whitespace-nowrap pl-[1px]'>
                              <span className='truncate text-base'>
                                {t('selectAll')}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </ListboxOption>
                  )}
                  {filtered.map((site) => (
                    <ListboxOption
                      key={site.id}
                      value={site}
                      className={({ active }) =>
                        classNames(
                          'relative cursor-default select-none text-primary active:bg-shade-12',
                          active
                            ? 'bg-shade-10'
                            : isItemSelected(site)
                            ? 'bg-shade-15'
                            : ''
                        )
                      }
                    >
                      {({ focus, selected: itemSelected }) => (
                        <div className='flex items-center'>
                          {!forceSingleMode && (
                            <div
                              className='flex items-center justify-center pl-4'
                              onClick={(e) => {
                                e.stopPropagation();
                                handleItemClick(site, true);
                              }}
                            >
                              <input
                                className={classNames(
                                  'z-30 h-4 w-4 cursor-pointer accent-brand',
                                  {
                                    'outline outline-[1.3px] outline-white':
                                      focus && isItemSelected(site),
                                  }
                                )}
                                type='checkbox'
                                checked={isItemSelected(site)}
                                onChange={() => {}}
                              />
                            </div>
                          )}
                          <div
                            className={classNames(
                              'flex flex-1 items-center justify-between truncate',
                              forceSingleMode ? 'px-4 py-3' : 'px-2 py-2'
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleItemClick(site, false);
                            }}
                          >
                            <div
                              className={classNames(
                                'flex items-center gap-2 truncate whitespace-nowrap',
                                !forceSingleMode && 'pl-[1px]'
                              )}
                            >
                              {site.image ? (
                                <Image url={site.image} />
                              ) : (
                                <BuildingPlaceholder
                                  className={classNames(
                                    '!h-6 !w-6 rounded-[3px] px-0 text-base',
                                    focus && 'bg-shade-20'
                                  )}
                                />
                              )}
                              <span
                                className={classNames(
                                  'truncate text-base',
                                  (forceSingleMode
                                    ? itemSelected
                                    : isItemSelected(site)) && 'font-semibold'
                                )}
                              >
                                {site.name}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </ListboxOption>
                  ))}
                </div>
                {!forceSingleMode && selected.length > 0 && (
                  <div className='sticky bottom-0 z-10 flex items-center justify-between border-t bg-white p-2 text-xs'>
                    <button
                      className='flex-1 p-2 text-left uppercase'
                      onClick={() => {
                        setIsMultipleMode(false);
                        handleClose();
                      }}
                    >
                      {t('cancel')}
                    </button>
                    <button
                      ref={confirmBtnRef}
                      className='flex-1 p-2 text-right uppercase'
                      onClick={handleClose}
                    >
                      {t('ok')}
                    </button>
                  </div>
                )}
              </ListboxOptions>
            </MenuTransition>
          </>
        )}
      </Listbox>
      {error && <p className='ErrorMessage'>{error}</p>}
    </Field>
  );
}

export { getAttributeFilterOptions } from './siteAttributeFilters';
